import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import { useSnackbar } from "notistack";

import { downloadActivityCsv, useActivity } from "@app/api/activity";
import type { ActivityFilterType, ActivityType } from "@app/api/types";
import { extractErrorMessage } from "@app/api/client";
import { ErrorMessage, Loading } from "@app/components/Feedback";
import { ROWS_PER_PAGE_OPTIONS } from "@app/constants";

const TYPE_LABEL: Record<ActivityType, string> = {
  "user.created": "User",
  "organization.created": "Organization",
  "game.created": "Game"
};

const TYPE_COLOR: Record<ActivityType, "primary" | "secondary" | "default"> = {
  "user.created": "primary",
  "organization.created": "secondary",
  "game.created": "default"
};

const ALL_FILTERS: ActivityFilterType[] = ["user", "organization", "game"];

export default function ActivityPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [types, setTypes] = useState<ActivityFilterType[]>(ALL_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading, error } = useActivity({
    offset: page * rowsPerPage,
    limit: rowsPerPage,
    types
  });

  const onExport = async (): Promise<void> => {
    try {
      await downloadActivityCsv({ types });
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err, "Export failed"), { variant: "error" });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Recent activity</Typography>
        <Button startIcon={<DownloadIcon />} variant="outlined" onClick={onExport}>
          Export CSV
        </Button>
      </Stack>

      <ToggleButtonGroup
        value={types}
        onChange={(_e, value: ActivityFilterType[]) => {
          setTypes(value.length ? value : ALL_FILTERS);
          setPage(0);
        }}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="user">Users</ToggleButton>
        <ToggleButton value="organization">Organizations</ToggleButton>
        <ToggleButton value="game">Games</ToggleButton>
      </ToggleButtonGroup>

      {isLoading && <Loading />}
      {error && <ErrorMessage error={error} />}

      {data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell>Organization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((item) => (
                <TableRow key={`${item.type}-${item.entityId}`} hover>
                  <TableCell>{new Date(item.at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip size="small" label={TYPE_LABEL[item.type]} color={TYPE_COLOR[item.type]} />
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.subtitle ?? "—"}</TableCell>
                  <TableCell>{item.organizationName ?? "—"}</TableCell>
                </TableRow>
              ))}
              {data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No activity
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.total ?? -1}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}
    </Box>
  );
}
